<template>
  <div v-if="product">
    <p class="text-center">
      <b>{{ product.name }}</b> - {{ $t('messages.selectBookingdate') }}
    </p>
    <div id="_pg-calendar">
      <VCalendar
          ref="calendar"
          :from-date="fromDate"
          :to-date="maxDate"
          :attributes="attributes"
          is-inline
          is-expanded
          @dayclick="onDayClick"
      />
      <div class="legend">
        <div class="legend-item">
          <div class="legend-color" style="background-color: #f6b4bf"></div>
          <div class="legend-text">{{ $t('messages.soldOut') }}</div>
        </div>
        <div class="legend-item">
          <div class="legend-color" style="background-color: #f8df81"></div>
          <div class="legend-text">{{ $t('messages.lastUnits') }}</div>
        </div>
        <div class="legend-item">
          <div class="legend-color" style="background-color: #9bd0b7"></div>
          <div class="legend-text">{{ $t('messages.available') }}</div>
        </div>
      </div>
      <div v-if="availableHours" class="eventsContainer">
        <div class="header">{{ formatDate(date) }}</div>
        <div class="events">
          <div
              v-for="event in availableHours.events"
              :key="event.startsAt"
              class="event-wrapper"
          >
            <div :class="'event ' + event.status" @click="selectEvent(event)">
              <span class="eventTimeRange">{{ event.startHour }} - {{ event.endHour }}</span><br>
              <span v-if="event.status === 'exceptionally_closed'">{{ $t('messages.exceptionallyClosed') }}</span>
              <span v-else>{{ $t('messages.xOfYAvailable', {x: event.bookingsAvailable, y: event.bookingsCapacity}) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="text-center">
      <button @click="cancel()" type="button" class="secondary">{{ $t('actions.cancel') }}</button>
    </div>
  </div>
</template>

<script>
import { Calendar as VCalendar } from 'v-calendar'
import BookingCalendarRepository from '@/api/BookingCalendarRepository'
import filterMixin from "@/mixins/filterMixin";

export default {
  components: {
    VCalendar
  },
  mixins: [filterMixin],
  props: {
    product: {required: true},
    giftVoucher: {required: false},
    overrideMinimumMinutesInAdvanceForBooking: {required: false},
  },
  data() {
    return {
      date: null,
      config: {
        minimumMinutesInAdvanceForBooking: 0,
        maximumDaysOfBookingForecast: 4
      },
      events: null,
      datesMap: null,
      availableDates: [false],
      availableHours: null,
      fromDate: new Date()
    }
  },
  computed: {
    minDate() {
      let minimumMinutesInAdvanceForBooking = this.overrideMinimumMinutesInAdvanceForBooking || this.config.minimumMinutesInAdvanceForBooking;
      let dateTs = (new Date()).getTime();
      return new Date(dateTs + (minimumMinutesInAdvanceForBooking * 60000));
    },
    maxDate() {
      let date = new Date();
      date.setDate(date.getDate() + this.config.maximumDaysOfBookingForecast);
      if (this.giftVoucher) {
        date = new Date(this.giftVoucher.expiresAt);
      }
      return date;
    },
    attributes() {
      if (!this.datesMap) {
        return [];
      }

      let greenDates = [];
      let orangeDates = [];
      let redDates = [];

      Object.keys(this.datesMap).forEach(key => {

        let redQty = 0;
        let orangeQty = 0;
        let greenQty = 0;

        for (const event of this.datesMap[key].events) {
          if (event.status === "completed") {
            redQty++;
          } else if( event.status === "almost-complete") {
            orangeQty++;
          } else if (event.status === "reservable") {
            greenQty++;
          }
        }

        if (redQty > 0  && greenQty === 0 && orangeQty === 0) {
          redDates.push(key); // IMPORTANTE, rojo solo si no hay slots disponibles
        } else if(orangeQty > greenQty) {
          orangeDates.push(key);
        } else {
          greenDates.push(key);
        }

      });

      return [
        {
          dates: redDates,
          highlight: {
            color: 'red', // #f6b4bf
            fillMode: 'light',
          },
        },
        {
          dates: orangeDates,
          highlight: {
            color: 'orange', // #f8df81
            fillMode: 'light',
          }
        },
        {
          dates: greenDates,
          highlight: {
            color: 'green', // #9bd0b7
            fillMode: 'light',
          }
        }
      ];

    }
  },
  mounted() {
    this.reset();
  },

  async created() {
    console.log('🔍 SimpleCalendar created with product:', this.product);
    console.log('🔍 Product bookingCalendarId:', this.product.bookingCalendarId);

    try {
      let response = await BookingCalendarRepository.findById(this.product.bookingCalendarId);
      console.log('🔍 BookingCalendar response:', response);
      this.config.minimumMinutesInAdvanceForBooking = response.data.minimumMinutesInAdvanceForBooking;
      this.config.maximumDaysOfBookingForecast = response.data.maximumDaysOfBookingForecast;
      console.log('🔍 Config updated:', this.config);
      await this.getSlots();
    } catch (error) {
      console.error('🚨 Error loading booking calendar:', error);
    }
  },
  methods: {
    cancel() {
      this.reset();
      this.$emit('cancel');
    },
    reset(){
      this.date = null;
      this.events = null;
      this.datesMap = null;
      this.availableDates = [false];
      this.availableHours = null;
    },
    async getSlots(){
      console.log('🔍 Getting slots for product:', this.product.id);
      console.log('🔍 Date range - start:', this.minDate, 'end:', this.maxDate);

      this.reset();
      let start = this.minDate;
      let end = new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), this.maxDate.getDate()).toISOString();

      try {
        let response = await BookingCalendarRepository.findCalendarSlots(this.product.id, start, end);
        console.log('🔍 Calendar slots response:', response);
        this.events = response.data;
        console.log('🔍 Events loaded:', this.events);

        this.setDatesMap();
        console.log('🔍 Dates map set:', this.datesMap);

        this.setAvailableDates();
        console.log('🔍 Available dates:', this.availableDates);

        this.goToAvailableDates();
      } catch (error) {
        console.error('🚨 Error loading calendar slots:', error);
      }
    },
    formatDateId(date){
      let year = date.getFullYear() + '';
      let month = (date.getMonth()+1) + '';
      month = (month.length === 2) ? month : '0' + month;
      let day = date.getDate() + '';
      day = (day.length === 2) ? day : '0'+day;

      return year + '-' + month + '-' + day;
    },
    formatTime(date) {
      let hour = date.getHours() + '';
      hour = (hour.length === 2) ? hour : '0'+hour;
      let minutes = date.getMinutes() + '';
      minutes = (minutes.length === 2) ? minutes : '0' + minutes;

      return hour + ':' + minutes;
    },
    convertTZ(date, tzString) {
      return new Date((typeof date === "string" ? new Date(date) : date).toLocaleString("en-US", {timeZone: tzString}));
    },
    mapSlot(slot){
      slot.startDate = this.convertTZ(slot.startsAt, 'Europe/Madrid');
      slot.endDate = this.convertTZ( new Date(Date.parse(slot.endsAt) + 1000) , 'Europe/Madrid');
      slot.date = this.convertTZ(slot.startDate, 'Europe/Madrid');
      //TODO maintain hours and minutes when api gets fixed
      slot.date.setHours(0);
      slot.date.setMinutes(0);
      slot.date.setSeconds(0);
      slot.date.setMilliseconds(0);
      slot.startHour = this.formatTime(slot.startDate);
      slot.endHour = this.formatTime(slot.endDate);
      const slotStartTS = Date.parse(slot.startsAt);
      const slotEndTS = Date.parse(slot.endsAt);
      slot.id = slotStartTS + '-' + slotEndTS;
      slot.dateId = this.formatDateId(slot.date);

      // fill the slot with the basket bookings that periods overlaps
      let bookingOrderDetails = this.$store.getters['basket/bookings'](this.product.bookingCalendarId);
      bookingOrderDetails.forEach(bookingOrderDetail => {
        const bodStartTS = Date.parse(bookingOrderDetail.booking.startsAt);
        const bodEndTS = Date.parse(bookingOrderDetail.booking.endsAt);
        if (this.periodsOverlaps(bodStartTS, bodEndTS, slotStartTS, slotEndTS)) {
          slot.bookingsOccupied++;
          slot.bookingsAvailable--;
        }
      });

      slot.status = 'reservable'
      if (slot.bookingsOccupied >= slot.bookingsAvailable) {
        slot.status = 'almost-complete'
      }
      if (!slot.bookingsAvailable) {
        slot.status = 'completed'
      }

      return slot;
    },
    periodsOverlaps(
        startTs1,
        endTs1,
        startTs2,
        endTs2
    ) {
      return !((endTs1 < startTs2 || startTs1 > endTs2));
    },
    setDatesMap() {

      if (!this.events) {
        this.datesMap = null;
      }

      let datesMap = {};

      this.events.map(ev => this.mapSlot(ev))
          .forEach(ev => {
            if (!datesMap[ev.dateId]) {
              datesMap[ev.dateId] = {
                available: false,
                exceptionally_closed: 0,
                completed: 0,
                reservable: 0,
                events: []
              };
            }
            datesMap[ev.dateId].events.push(ev);
          });

      for (const key in datesMap) {

        for (const ev of datesMap[key].events) {
          if (ev.status === 'completed') {
            datesMap[key].completed++
          } else if (ev.status === 'reservable' || ev.status === 'almost-complete') {
            datesMap[key].reservable++
          }
        }

        datesMap[key].available = !(datesMap[key].reservable === 0 && datesMap[key].completed === 0);

      }

      this.datesMap = datesMap;
    },
    setAvailableDates() {
      if (!this.datesMap) {
        return [];
      }
      this.availableDates = Object.keys(this.datesMap).filter(key => {
        return this.datesMap[key].available
      });
    },
    goToAvailableDates() {
      if (!this.availableDates || !this.availableDates.length) {
        return;
      }
      let firstAvailableDate = this.availableDates[0];
      // En v-calendar v3.x, navegamos cambiando fromDate
      try {
        this.fromDate = new Date(firstAvailableDate);
      } catch (error) {
        console.log('🔧 Could not navigate to first available date:', error);
        // Fallback: usar minDate si hay error
        this.fromDate = this.minDate;
      }
    },
    onDayClick(day) {
      this.date = day.date
      this.availableHours = null;
      // En v-calendar v3.x, el id del día se obtiene de forma diferente
      let dayId = this.formatDateId(day.date);
      if (this.datesMap && this.datesMap[dayId] && this.datesMap[dayId].available) {
        let availableHours = this.datesMap[dayId];
        // sort events by startsAt
        availableHours.events.sort((a, b) => {
          return new Date(a.startsAt) - new Date(b.startsAt)
        });
        this.availableHours = availableHours;
      }
    },
    selectEvent(event) {
      if (event.bookingsAvailable) {
        this.reset();
        this.$emit('bookingSlotSelected', event);
      }
    },
    fixCalendarLayout() {
      // Buscar y corregir el layout de los días de la semana
      const calendarEl = this.$el.querySelector('#_pg-calendar');
      if (!calendarEl) return;

      // Buscar todos los posibles contenedores de días de la semana
      const weekdaySelectors = [
        '.vc-weekdays',
        '.vc-weekday-row',
        '.weekdays',
        '.weekday-row',
        '[class*="weekday"]:not([class*="weekday-"]):has(> *)',
        'div:has(> *:nth-child(7):last-child):not(.events)'
      ];

      weekdaySelectors.forEach(selector => {
        try {
          const weekdayContainer = calendarEl.querySelector(selector);
          if (weekdayContainer && weekdayContainer.children.length === 7) {
            console.log('🔧 Fixing weekday container:', selector);
            weekdayContainer.style.cssText = `
              width: 100% !important;
              display: grid !important;
              grid-template-columns: repeat(7, 1fr) !important;
              margin: 0 !important;
              padding: 0 !important;
              gap: 0 !important;
            `;

            // Corregir cada día individual
            Array.from(weekdayContainer.children).forEach(child => {
              child.style.cssText = `
                text-align: center !important;
                padding: 8px 0 !important;
                font-weight: bold !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                width: 100% !important;
              `;
            });
          }
        } catch (e) {
          // Ignorar errores de selectores no válidos
        }
      });

      // También aplicar al componente completo
      const vcContainer = calendarEl.querySelector('*[class*="vc-"]');
      if (vcContainer) {
        vcContainer.style.width = '100%';
      }
    }
  }
}
</script>

<style scoped>
#_pg-calendar {
  z-index: 99999999;
  position: relative;
  margin: 0 auto 20px;
  width: 100%;
  max-width: none;
}

/* Forzar que el componente v-calendar ocupe todo el ancho */
#_pg-calendar :deep(.vc-container) {
  width: 100% !important;
}

#_pg-calendar :deep(.vc-pane-container) {
  width: 100% !important;
}

#_pg-calendar :deep(.vc-pane) {
  width: 100% !important;
}

#_pg-calendar :deep(.vc-header) {
  width: 100% !important;
}

/* Estilos específicos SOLO para los días de la semana (L M X J V S D) */
#_pg-calendar :deep(.vc-weekdays) {
  display: flex !important;
  width: 100% !important;
}

#_pg-calendar :deep(.vc-weekday) {
  flex: 1 !important;
  text-align: center !important;
  padding: 8px 0 !important;
  font-weight: bold !important;
}

/* NO tocar el grid de weeks - dejar que v-calendar lo maneje naturalmente */
#_pg-calendar :deep(.vc-weeks) {
  width: 100% !important;
}

#_pg-calendar .eventsContainer {
  border: 1px solid #bbb;
  border-radius: 8px;
  margin-top: 10px;
  background-color: #fff;
  padding: 10px;
  width: 100%;
}

#_pg-calendar .eventsContainer .header {
  text-align: center;
  font-weight: bold;
  padding-bottom: 5px;
}

#_pg-calendar .events {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
}

#_pg-calendar .event-wrapper {
  width: 33.333333%;
  padding: 5px 10px;
}

#_pg-calendar .event {
  color: #fff;
  padding: 5px 10px;
  border-radius: 4px;
  text-align: center;
}

#_pg-calendar .reservable {
  background-color: #38a169;
  cursor: pointer;
}

#_pg-calendar .almost-complete {
  background-color: #e8b151;
  cursor: pointer;
}

#_pg-calendar .completed {
  background-color: #f56565;
  cursor: not-allowed;
}

@media (max-width: 767px) {
  #_pg-calendar .event-wrapper {
    width: 50%;
  }
}

@media (max-width: 374px) {
  #_pg-calendar .event-wrapper {
    width: 100%;
  }
}

button {
  border: none;
  border-radius: 8px;
  color: #fff;
  padding: 6px 12px;
  background: #c91919;
}

button.secondary {
  background: #acacac;
  color: #fff;
  padding: 0 25px;
  line-height: 40px;
  font-size: 14px;
  border-radius: 3px;
}

.eventTimeRange {
  font-weight: bold;
}

p.text-center {
  font-family: inherit;
  color: #464958;
}

.legend {
  margin-top: 10px;
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  width: 100%;
}

.legend-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.legend-color {
  width: 20px;
  height: 20px;
  border-radius: 10px;
  margin-right: 5px;
}
</style>
<style>
.cremei-calendar .vc-bg-green-200 {
  background-color: #07D158;
  background-position-y: bottom;
  height: 4px !important;
  width: 20px !important;
  margin-top: 25px;
}
.cremei-calendar .vc-bg-orange-200{
  background-color: #F4B30D;
  background-position-y: bottom;
  height: 4px !important;
  width: 20px !important;
  margin-top: 25px;
}
.cremei-calendar .vc-bg-red-200{
  background-color: #FF0000;
  background-position-y: bottom;
  height: 4px !important;
  width: 20px !important;
  margin-top: 25px;
}
.cremei-calendar .vc-text-gray-500{
  color:#000;
}
button.secondary {
  color: #CCA20E !important;
  text-align: center;
  font-family: "Open Sans";
  font-size: 16px;
  font-style: normal;
  font-weight: 400 !important;
  line-height: 16px; /* 100% */
  background: none !important;
  border: 1px solid #CCA20E;
  border-radius: 10px;
  text-transform: uppercase;
}
.cremei-calendar.vc-border-gray-400{
  border: 2px solid #CCA20E;
  border-radius: 0 !important;
}
table.cremei-table-basket,
table.cremei-table-basket td, table.cremei-table-basket th{
  border:0 !important;
}
</style>